export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

function getAuthHeaders(contentType: string | null = 'application/json') {
  const token = localStorage.getItem('access_token');
  const headers: HeadersInit = {};
  
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

export interface Citation {
  id: number;
  title: string | null;
  url: string | null;
  type: string | null;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export interface SearchResponse {
  answer: string;
  citations: Citation[];
}

export interface User {
  id: string;
  userId: string;
  email: string;
  name: string;
  title?: string;
  userType: 'user' | 'cadmin' | 'superadmin';
  isActive: boolean;
  status: string;
  [key: string]: any;
}

export interface Project {
  id: string;
  userId: string;
  name: string;
  type?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  status: string;
}

export async function getProject(projectId: string, userId: string): Promise<Project> {
  const response = await fetch(`${API_BASE_URL}/projects/${projectId}?userId=${userId}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch project');
  }

  return response.json();
}

export interface ChatSession {
  id: string;
  userId: string;
  projectId?: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: any[];
}

export async function search(query: string, history: Message[] = [], userId?: string, projectId?: string, projectType?: string): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/search/query`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      query,
      history: history.map(({ role, content }) => ({ 
        role, 
        content: typeof content === 'string' ? content : JSON.stringify(content)
      })),
      user_id: userId,
      project_id: projectId,
      project_type: projectType
    }),
  });

  if (!response.ok) {
    throw new Error('Search request failed');
  }

  return response.json();
}

export async function login(email: string, password?: string): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/users/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, provider: 'local' })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }));
    throw new Error(error.detail || 'Login failed');
  }
  
  const data = await response.json();
  if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
  }
  return data.user;
}

export async function register(data: { name: string; email: string; password: string; title?: string; userType: string }): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/users/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
    throw new Error(error.detail || 'Registration failed');
  }

  return response.json();
}

export async function resetPassword(email: string, new_password: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/users/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, new_password })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Password reset failed' }));
    throw new Error(error.detail || 'Password reset failed');
  }

  return response.json();
}

export async function createProject(name: string, description: string, userId: string, type?: string): Promise<Project> {
  const response = await fetch(`${API_BASE_URL}/projects/?userId=${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name, description, type })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create project' }));
    throw new Error(error.detail || 'Failed to create project');
  }

  return response.json();
}

export async function getProjects(userId: string): Promise<Project[]> {
  const response = await fetch(`${API_BASE_URL}/projects/?userId=${userId}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch projects' }));
    throw new Error(error.detail || 'Failed to fetch projects');
  }

  return response.json();
}

export async function updateProject(projectId: string, name: string, description: string, userId: string): Promise<Project> {
  const response = await fetch(`${API_BASE_URL}/projects/${projectId}?userId=${userId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name, description })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update project' }));
    throw new Error(error.detail || 'Failed to update project');
  }

  return response.json();
}

export async function deleteProject(projectId: string, userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/projects/${projectId}?userId=${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete project' }));
    throw new Error(error.detail || 'Failed to delete project');
  }
}

export async function generateProjectName(projectId: string, query: string, userId: string): Promise<Project> {
  const response = await fetch(`${API_BASE_URL}/projects/${projectId}/generate-name?userId=${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ query })
  });

  if (!response.ok) {
    throw new Error('Failed to generate project name');
  }

  return response.json();
}

export async function createChatSession(userId: string, projectId?: string, title: string = "New Chat"): Promise<ChatSession> {
  const params = new URLSearchParams({ userId, title });
  if (projectId) params.append('projectId', projectId);
  
  const response = await fetch(`${API_BASE_URL}/chats/?${params.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to create chat session');
  }

  return response.json();
}

export async function getChatSessions(userId: string, projectId?: string): Promise<ChatSession[]> {
  const params = new URLSearchParams({ userId });
  if (projectId) params.append('projectId', projectId);

  const response = await fetch(`${API_BASE_URL}/chats/?${params.toString()}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch chat sessions');
  }

  return response.json();
}

export async function addChatMessage(sessionId: string, userId: string, message: { role: string, content: string }): Promise<ChatSession> {
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/messages?userId=${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(message)
  });

  if (!response.ok) {
    throw new Error('Failed to add message');
  }

  return response.json();
}

export async function updateChatSessionTitle(sessionId: string, userId: string, title: string): Promise<ChatSession> {
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/title?userId=${userId}&title=${encodeURIComponent(title)}`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to update session title');
  }

  return response.json();
}

export async function bookmarkUseCase(useCase: any, userId: string, projectId?: string): Promise<any> {
    const payload = { ...useCase, userId, projectId, isBookmarked: true };
    const response = await fetch(`${API_BASE_URL}/use-cases/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
        throw new Error('Failed to bookmark use case');
    }
    
    return response.json();
}

export async function bookmarkStartup(startup: any, userId: string, projectId?: string): Promise<any> {
    const payload = { ...startup, userId, projectId };
    const response = await fetch(`${API_BASE_URL}/startups/bookmark`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
        throw new Error('Failed to bookmark startup');
    }
    
    return response.json();
}

export async function bookmarkProduct(product: any, userId: string, projectId?: string): Promise<any> {
    const payload = { ...product, userId, projectId };
    const response = await fetch(`${API_BASE_URL}/products/bookmark`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
        throw new Error('Failed to bookmark product');
    }
    
    return response.json();
}

export async function getBookmarkedUseCases(projectId: string): Promise<any[]> {
  const url = `${API_BASE_URL}/use-cases/?projectId=${projectId}`;
  
  const response = await fetch(url, {
      headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch bookmarked use cases');
  }

  return response.json();
}

export async function getBookmarkedStartups(projectId: string): Promise<any[]> {
  const url = `${API_BASE_URL}/startups/saved?projectId=${projectId}`;
  
  const response = await fetch(url, {
      headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch bookmarked startups');
  }

  return response.json();
}

export async function getBookmarkedProducts(projectId: string): Promise<any[]> {
  const url = `${API_BASE_URL}/products/saved?projectId=${projectId}`;
  
  const response = await fetch(url, {
      headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch bookmarked products');
  }

  return response.json();
}

export async function bookmarkLead(lead: any, userId: string, projectId?: string): Promise<any> {
    const payload = { ...lead, userId, projectId };
    const response = await fetch(`${API_BASE_URL}/leads/bookmark`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
        throw new Error('Failed to bookmark lead');
    }
    
    return response.json();
}

export async function getBookmarkedLeads(projectId: string): Promise<any[]> {
  const url = `${API_BASE_URL}/leads/saved?projectId=${projectId}`;
  
  const response = await fetch(url, {
      headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch bookmarked leads');
  }

  return response.json();
}

export async function generateReport(sessionId: string, userId: string, selectedSectionIds?: string[], generationNotes?: string): Promise<{report: string}> {
  const body: Record<string, unknown> = { selected_section_ids: selectedSectionIds };
  if (generationNotes != null && generationNotes.trim() !== '') {
    body.generation_notes = generationNotes.trim();
  }
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/report?userId=${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error('Failed to generate report');
  }

  return response.json();
}

export async function generateTable(sessionId: string, userId: string, selectedSectionIds?: string[], generationNotes?: string): Promise<{table: string}> {
  const body: Record<string, unknown> = { selected_section_ids: selectedSectionIds };
  if (generationNotes != null && generationNotes.trim() !== '') {
    body.generation_notes = generationNotes.trim();
  }
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/table?userId=${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error('Failed to generate table');
  }

  return response.json();
}

export async function generateProductStructure(sessionId: string, userId: string, selectedSectionIds?: string[], selectedProduct?: any): Promise<{product_structure: string}> {
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/product_structure?userId=${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ selected_section_ids: selectedSectionIds, selected_product: selectedProduct })
  });

  if (!response.ok) {
    throw new Error('Failed to generate product structure');
  }

  return response.json();
}

export type ProductDesignImageKinds = {
  wantFinalProductImage: boolean;
  wantTechnicalDrawing: boolean;
};

export type ProductDesignVariant = {
  kind: 'final_product' | 'technical';
  image_base64?: string;
  image_url?: string;
  prompt?: string;
};

export type ProductDesignResult = {
  error?: string;
  prompt?: string;
  image_base64?: string;
  image_url?: string;
  kind?: string;
  variants?: ProductDesignVariant[];
};

export async function generateProductDesign(
  sessionId: string,
  userId: string,
  selectedSectionIds?: string[],
  selectedProduct?: any,
  projectId?: string,
  imageKinds?: ProductDesignImageKinds
): Promise<ProductDesignResult> {
  const qs = new URLSearchParams({ userId });
  if (projectId) qs.set('projectId', projectId);
  const body: Record<string, unknown> = {
    selected_section_ids: selectedSectionIds,
    selected_product: selectedProduct,
  };
  if (imageKinds) {
    body.want_final_product_image = imageKinds.wantFinalProductImage;
    body.want_technical_drawing = imageKinds.wantTechnicalDrawing;
  }
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/product_design?${qs.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('Failed to generate product design');
  }

  return response.json();
}

export async function generateProductDesignFromDescription(
  userId: string,
  description: string,
  projectId?: string,
  imageKinds?: ProductDesignImageKinds
): Promise<ProductDesignResult> {
  const qs = new URLSearchParams({ userId });
  if (projectId) qs.set('projectId', projectId);
  const body: Record<string, unknown> = { description };
  if (imageKinds) {
    body.want_final_product_image = imageKinds.wantFinalProductImage;
    body.want_technical_drawing = imageKinds.wantTechnicalDrawing;
  }
  const response = await fetch(`${API_BASE_URL}/chats/product_design_from_description?${qs.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('Failed to generate product design from description');
  }

  return response.json();
}

export type SessionExtractedProduct = {
  name: string;
  description: string;
  features: string[];
  target_audience: string;
  competitors: string[];
  market_gap: string;
};

/** LLM: distinct products mentioned anywhere in the chat session. */
export async function extractProductsFromSession(
  sessionId: string,
  userId: string
): Promise<{ products: SessionExtractedProduct[]; error?: string }> {
  const qs = new URLSearchParams({ userId });
  const response = await fetch(`${API_BASE_URL}/chats/${sessionId}/extract_products?${qs.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to extract products from session');
  }

  return response.json();
}

export async function generateCustomerTable(userId: string, projectId: string | undefined, filters: Record<string, string>): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/tables/generate_customer`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, projectId, filters })
  });

  if (!response.ok) {
    throw new Error('Failed to generate customer table');
  }

  return response.json();
}

export async function editReport(currentReport: string, instruction: string, chatHistory: Message[] = []): Promise<{updated_report: string, explanation: string}> {
  const response = await fetch(`${API_BASE_URL}/chats/report/edit`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ current_report: currentReport, instruction, chat_history: chatHistory }),
  });

  if (!response.ok) {
    throw new Error('Failed to edit report');
  }

  return response.json();
}

export async function regenerateProductDesign(
  currentReportMarkdown: string,
  instruction: string,
  chatHistory: Message[],
  userId: string,
  projectId?: string
): Promise<{ updated_report: string; explanation: string }> {
  const qs = new URLSearchParams({ userId });
  if (projectId) qs.set('projectId', projectId);
  const response = await fetch(`${API_BASE_URL}/chats/regenerate_product_design?${qs.toString()}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      current_report_markdown: currentReportMarkdown,
      instruction,
      chat_history: chatHistory.map((m) => ({ role: m.role, content: m.content })),
    }),
  });

  if (!response.ok) {
    let detail = 'Görsel yenileme başarısız';
    try {
      const j = (await response.json()) as { detail?: string | unknown };
      if (typeof j.detail === 'string') detail = j.detail;
      else if (j.detail !== undefined) detail = JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }

  return response.json();
}

export type ReportFrequency = 'weekly' | 'monthly' | 'quarterly';

export interface Report {
  id: string;
  userId: string;
  projectId?: string;
  title: string;
  content: string;
  topic?: string;
  frequency?: ReportFrequency;
  nextRunAt?: string;
  isActive?: boolean;
  parentId?: string;
  createdAt: string;
  updatedAt: string;
}

export async function saveReport(
  userId: string,
  projectId: string | undefined,
  title: string,
  content: string,
  topic?: string,
  frequency?: ReportFrequency
): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/reports/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, projectId, title, content, topic, frequency })
  });
  if (!response.ok) throw new Error('Failed to save report');
  return response.json();
}

export async function saveTable(
  userId: string,
  projectId: string | undefined,
  title: string,
  content: string,
  topic?: string
): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/tables/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, projectId, title, content, topic })
  });
  if (!response.ok) throw new Error('Failed to save table');
  return response.json();
}

export async function updateReport(reportId: string, userId: string, content: string, title?: string): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/reports/${reportId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, content, title })
  });
  if (!response.ok) throw new Error('Failed to update report');
  return response.json();
}

export async function updateTable(tableId: string, userId: string, content: string, title?: string): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/tables/${tableId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, content, title })
  });
  if (!response.ok) throw new Error('Failed to update table');
  return response.json();
}

export async function getReports(userId: string, projectId?: string): Promise<Report[]> {
  const params = new URLSearchParams({ userId });
  if (projectId) params.append('projectId', projectId);
  params.append('_t', Date.now().toString()); // prevent caching
  const response = await fetch(`${API_BASE_URL}/reports/?${params.toString()}`, {
      headers: { ...getAuthHeaders(), 'Cache-Control': 'no-cache' }
  });
  if (!response.ok) throw new Error('Failed to fetch reports');
  return response.json();
}

export async function getTables(userId: string, projectId?: string): Promise<Report[]> {
  const params = new URLSearchParams({ userId });
  if (projectId) params.append('projectId', projectId);
  params.append('_t', Date.now().toString()); // prevent caching
  const response = await fetch(`${API_BASE_URL}/tables/?${params.toString()}`, {
      headers: { ...getAuthHeaders(), 'Cache-Control': 'no-cache' }
  });
  if (!response.ok) throw new Error('Failed to fetch tables');
  return response.json();
}

export async function getReport(reportId: string, userId: string): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/reports/${reportId}?userId=${userId}`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch report');
  return response.json();
}

export async function getTable(tableId: string, userId: string): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/tables/${tableId}?userId=${userId}`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch table');
  return response.json();
}

export async function generateReportFromTopic(reportId: string, userId: string): Promise<Report> {
  const response = await fetch(`${API_BASE_URL}/reports/${reportId}/generate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) throw new Error('Failed to generate report');
  return response.json();
}

export async function uploadDocument(file: File, userId: string, projectId: string): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('user_id', userId);
  formData.append('project_id', projectId);

  const response = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    headers: getAuthHeaders(null), // null for Content-Type to let browser set boundary
    body: formData, 
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

export interface ProjectDocument {
    name: string;
    url: string;
    size: number;
    last_modified: string;
}

export async function getProjectDocuments(userId: string, projectId: string): Promise<ProjectDocument[]> {
    const params = new URLSearchParams({ user_id: userId, project_id: projectId });
    const response = await fetch(`${API_BASE_URL}/documents/?${params.toString()}`, {
        headers: getAuthHeaders()
    });
    if (!response.ok) {
        throw new Error('Failed to fetch documents');
    }
    return response.json();
}

export async function submitEarlyAccess(email: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/early-access/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to join early access' }));
    throw new Error(error.detail || 'Failed to join early access');
  }
  return response.json();
}

export async function researchCompany(data: { name: string; website: string; location: string }): Promise<{ profile: string }> {
  const response = await fetch(`${API_BASE_URL}/companies/research`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Research failed' }));
    throw new Error(error.detail || 'Research failed');
  }

  return response.json();
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  ownerUserId: string;
  profile?: {
    description?: string;
    summary?: string;
    industry?: string;
    location?: string;
    website?: string;
    products?: string[];
    mission?: string;
    vision?: string;
    values?: string[];
    foundedYear?: string;
    employeeCount?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export interface Competitor {
  id: string;
  companyId: string;
  name: string;
  website?: string;
  type: string;
  description?: string;
  differentiation?: string;
  strengths?: string[];
  weaknesses?: string[];
  marketCap?: string;
}

export interface MarketResearch {
  id: string;
  companyId: string;
  marketOverview?: string;
  marketSize?: string;
  targetAudience?: string[];
}

export interface MarketNews {
  id: string;
  companyId: string;
  month: number;
  year: number;
  news: string[];
  trends: string[];
  opportunities: string[];
  threats: string[];
  summary: string;
}

export interface CompetitorNewsItem {
  competitorName: string;
  news: string[];
  summary: string;
}

export interface CompetitorNews {
  id: string;
  companyId: string;
  month: number;
  year: number;
  competitors: CompetitorNewsItem[];
  summary: string;
  createdAt: string;
}

export class AuthError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

export async function getUserCompanies(): Promise<Company[]> {
  const response = await fetch(`${API_BASE_URL}/companies/user/me`, {
      headers: getAuthHeaders()
  });
  if (response.status === 401 || response.status === 403) {
    throw new AuthError('Session expired. Please log in again.', response.status);
  }
  if (!response.ok) throw new Error('Failed to fetch companies');
  return response.json();
}

export async function getCompany(companyId: string): Promise<Company> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch company');
  return response.json();
}

export async function getCompanyCompetitors(companyId: string): Promise<Competitor[]> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/competitors`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch competitors');
  return response.json();
}

export async function addCompanyCompetitor(companyId: string, competitor: Partial<Competitor>): Promise<Competitor> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/competitors`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(competitor)
  });
  if (!response.ok) throw new Error('Failed to add competitor');
  return response.json();
}

export async function updateCompanyCompetitor(companyId: string, competitorId: string, competitor: Partial<Competitor>): Promise<Competitor> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/competitors/${competitorId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(competitor)
  });
  if (!response.ok) throw new Error('Failed to update competitor');
  return response.json();
}

export async function deleteCompanyCompetitor(companyId: string, competitorId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/competitors/${competitorId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to delete competitor');
}

export async function getCompanyMarketResearch(companyId: string): Promise<MarketResearch[]> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/market-research`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch market research');
  return response.json();
}

export async function getCompanyMarketNews(companyId: string): Promise<MarketNews[]> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/market-news`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch market news');
  return response.json();
}

export async function getCompanyCompetitorNews(companyId: string): Promise<CompetitorNews[]> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/competitor-news`, {
      headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch competitor news');
  return response.json();
}

export async function researchCompetitorNews(companyId: string): Promise<CompetitorNews> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/competitor-news/research`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to research competitor news');
  return response.json();
}

export async function updateCompanyProfile(companyId: string, profile: any): Promise<Company> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/profile`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(profile)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update company profile');
  }
  
  return response.json();
}

export async function createCompany(
  name: string, 
  ownerUserId: string, 
  slug: string,
  profile?: any
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/companies/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ 
      name,
      slug,
      profile
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Company creation failed' }));
    throw new Error(error.detail || 'Company creation failed');
  }

  return response.json();
}

export async function getCompanyUsers(companyId: string): Promise<any[]> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/users`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch company users');
  }
  
  return response.json();
}

export async function inviteUserToCompany(companyId: string, email: string, role: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/companies/${companyId}/invites`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, role })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to invite user' }));
    throw new Error(error.detail || 'Failed to invite user');
  }

  return response.json();
}

export async function getMyInvites(): Promise<any[]> {
  const response = await fetch(`${API_BASE_URL}/users/me/invites`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch invites');
  }

  return response.json();
}

export async function acceptInvite(inviteId: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/users/me/invites/${inviteId}/accept`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to accept invite' }));
    throw new Error(error.detail || 'Failed to accept invite');
  }

  return response.json();
}

export async function rejectInvite(inviteId: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/users/me/invites/${inviteId}/reject`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to reject invite' }));
    throw new Error(error.detail || 'Failed to reject invite');
  }

  return response.json();
}
